import React from 'react';
import { useTranslation } from 'react-i18next';

import { Grid, Typography } from '@mui/material';
import { DATE_FORMAT_MMM_D_YYYY } from 'constants/formats';
import dayjs from 'dayjs';
import { useGetPaymentMethod, useGetPaymentsHistory } from 'services/payments/payments/hooks';
import { PaymentsApi } from 'services/payments/payments/payments.api';
import { useUserSubscriptionStore } from 'store/useUserSubscriptionStore';
import { getUserSubscription } from 'store/useUserSubscriptionStore/selectors';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { PAYMENT_STATUS } from 'interfaces/payments';

import { AlertComponent } from 'components/_basic/AlertComponent';
import { Button } from 'components/_basic/Button/Button';
import { Card } from 'components/_basic/Card';
import LoadingDecorator from 'components/LoadingDecorator';
import { PaymentIcon } from 'components/PaymentIcon';

import { formatCurrency } from 'helpers/numbers';
import ToastService from 'helpers/ToastService';

import { getExpiredCardDateString } from './helpers';
import { IPaymentMethodProps } from './types';


import styles from './PaymentMethod.module.scss';

export const PaymentMethod: React.FC<IPaymentMethodProps> = ({ lastPayment, showFailAlert }) => {
  const { t, i18n } = useTranslation();
  const { track, isPending } = usePromiseStatus();

  const { data: dataPaymentsHistory, isPending: isPendingPaymentsHistory } = useGetPaymentsHistory();
  const { data, isLoading, error } = useGetPaymentMethod({
    enabled: !isPendingPaymentsHistory && !dataPaymentsHistory?.empty,
  });
  dayjs.locale(i18n.language);

  const cost = lastPayment?.order.subscription.price;
  const costToDisplay = cost ? formatCurrency(lastPayment?.order.subscription.price) : null;

  const currentSubscription = useUserSubscriptionStore(getUserSubscription);

  const endDate = currentSubscription?.currentSubscription.endDate;
  const unixDate = endDate ? dayjs.unix(endDate) : null;
  const endDateToDisplay = unixDate?.format(DATE_FORMAT_MMM_D_YYYY);

  const isFailed = showFailAlert && lastPayment?.status === PAYMENT_STATUS.FAILED;

  const handleChangePaymentMethodClick = async () => {
    try {
      const response = await track(PaymentsApi.getUrlToChangePaymentMethod());
      const changePaymentMethodUrl = response.url;

      window.location.href = changePaymentMethodUrl;
    } catch (error) {
      ToastService.showError(t('failedGetPaymentUrlError'));
    }
  };

  return (
    <Card title={t('paymentMethod')}>
      <LoadingDecorator data={data} loading={isLoading || isPendingPaymentsHistory} error={error}>
        {({ last4, expirationMonth, expirationYear, wallet, brand }) => (
          <>
            <Grid container spacing={2}>
              <Grid item xs={4} display="flex" alignItems="center" gap={1}>
                <PaymentIcon walletType={wallet ?? brand} />
                <Typography variant="body2" fontWeight={700}>
                  **** {last4}, {getExpiredCardDateString(expirationMonth, expirationYear)}
                </Typography>
              </Grid>
              <Grid item xs={8} display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                {costToDisplay && endDateToDisplay && !isFailed && (
                  <Typography variant="subtitle2">
                    {t('nextBillingAmount', { amount: costToDisplay })}

                    <Typography variant="subtitle2" fontWeight={700} component="span">
                      {endDateToDisplay}
                    </Typography>
                  </Typography>
                )}
                <Button
                  size="small"
                  isLoading={isPending}
                  className={styles.button}
                  onClick={handleChangePaymentMethodClick}
                >
                  {t(!isFailed ? 'view' : 'update')}
                </Button>
              </Grid>
            </Grid>

            {isFailed && (
              <AlertComponent
                severity="error"
                title="Last payment failed"
                message={t('lastPaymentErrorMessage', { date: endDateToDisplay })}
                sx={{ marginTop: 2 }}
              />
            )}
          </>
        )}
      </LoadingDecorator>
    </Card>
  );
};
